import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import ProductCard from '@/components/ProductCard';
import { ArrowRight, Newspaper, Globe, Share2, FileText, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        // Get featured products (one from each category)
        const categories = ['print', 'digital', 'social', 'advertorial'];
        const featured = categories.map(cat => 
          response.data.find(p => p.category === cat)
        ).filter(Boolean);
        setFeaturedProducts(featured);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const categories = [
    {
      id: 'print',
      title: 'Print Advertising',
      description: '12,000-16,000 biweekly copies across Los Cabos, La Paz & Todos Santos',
      icon: Newspaper,
      image: 'https://customer-assets.emergentagent.com/job_ad-shop/artifacts/stu0r9vk_Printed-Ads.jpg',
      href: '/products/print',
      stats: '12K+ Readers'
    },
    {
      id: 'digital',
      title: 'Website Ads',
      description: 'Premium banner placements with high visibility and engagement',
      icon: Globe,
      image: 'https://customer-assets.emergentagent.com/job_ad-shop/artifacts/mxx8ytvj_Sandwich.jpg',
      href: '/products/digital',
      stats: 'High CTR'
    },
    {
      id: 'social',
      title: 'Social Media',
      description: 'Reach our 19,000+ engaged followers on Facebook & Instagram',
      icon: Share2,
      image: 'https://customer-assets.emergentagent.com/job_ad-shop/artifacts/caba37p4_Social-Media.jpg',
      href: '/products/social',
      stats: '19K+ Followers'
    },
    {
      id: 'advertorial',
      title: 'Advertorial',
      description: 'Tell your story with a full-page article-style advertisement',
      icon: FileText,
      image: 'https://customer-assets.emergentagent.com/job_ad-shop/artifacts/0yak5h7p_Advertorial.jpg',
      href: '/products/advertorial',
      stats: 'Full Coverage'
    }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-navy-900 text-white overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url(https://images.unsplash.com/photo-1551406483-3731d1997540?w=1600)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="relative container mx-auto px-4 py-20 md:py-32">
          <div className="max-w-3xl">
            <p className="text-navy-300 text-sm font-medium tracking-wider uppercase mb-4 animate-fade-in">
              Gringo Gazette Advertising
            </p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight animate-slide-up">
              Reach Baja's Most Engaged 
              <span className="text-navy-300"> Audience</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 mb-8 font-body max-w-2xl animate-slide-up" style={{ animationDelay: '0.1s' }}>
              Advertise in Baja California Sur's trusted English-language newspaper. 
              Print, digital, and social media options to fit every budget.
            </p>
            <div className="flex flex-wrap gap-4 animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <Link to="/products">
                <Button size="lg" className="bg-white text-navy-900 hover:bg-slate-100 font-semibold" data-testid="browse-all-btn">
                  Browse All Options
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/products/print">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10" data-testid="print-ads-hero-btn">
                  Print Advertising
                </Button>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Stats bar */}
        <div className="relative bg-navy-950/50 border-t border-white/10">
          <div className="container mx-auto px-4 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="font-heading text-2xl md:text-3xl font-bold text-white">16,000+</div>
                <div className="text-sm text-slate-400">Print Copies</div>
              </div>
              <div>
                <div className="font-heading text-2xl md:text-3xl font-bold text-white">9,800+</div>
                <div className="text-sm text-slate-400">Email Subscribers</div>
              </div>
              <div>
                <div className="font-heading text-2xl md:text-3xl font-bold text-white">19,000+</div>
                <div className="text-sm text-slate-400">Social Followers</div>
              </div>
              <div>
                <div className="font-heading text-2xl md:text-3xl font-bold text-white">30+</div>
                <div className="text-sm text-slate-400">Years Trusted</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="categories-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Advertising Options
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto font-body">
              Choose from our range of advertising formats designed to maximize your reach in Baja California Sur
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
            {categories.map((category) => (
              <Link 
                key={category.id} 
                to={category.href}
                className="group"
                data-testid={`category-card-${category.id}`}
              >
                <Card className="overflow-hidden h-full border-0 shadow-md hover:shadow-xl transition-all duration-300 group-hover:-translate-y-1">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={category.image} 
                      alt={category.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-900/90 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="flex items-center gap-2 text-white mb-2">
                        <category.icon className="h-5 w-5" />
                        <span className="text-sm font-medium">{category.stats}</span>
                      </div>
                      <h3 className="font-heading text-xl font-bold text-white">{category.title}</h3>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-600 mb-3 font-body">{category.description}</p>
                    <div className="flex items-center text-navy-700 font-medium text-sm group-hover:text-navy-900">
                      View Options
                      <ChevronRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 md:py-24" data-testid="featured-section">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-2">
                Popular Ad Options
              </h2>
              <p className="text-slate-600 font-body">Our most requested advertising formats</p>
            </div>
            <Link to="/products">
              <Button variant="outline" className="hidden md:flex items-center gap-2 border-navy-200 hover:bg-navy-50">
                View All
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}

          <div className="mt-8 text-center md:hidden">
            <Link to="/products">
              <Button variant="outline" className="border-navy-200 hover:bg-navy-50">
                View All Products
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-navy-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            Ready to Grow Your Business?
          </h2>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8 font-body">
            Join hundreds of businesses that trust Gringo Gazette to connect them 
            with customers in Baja California Sur.
          </p>
          <Link to="/products">
            <Button size="lg" className="bg-white text-navy-900 hover:bg-slate-100 font-semibold" data-testid="get-started-btn">
              Get Started Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
